import { Duration } from 'luxon';
import {
  Filters,
  Place,
  VirtualLocation,
  SelectedLocation,
  DurationFilter,
  PhysicalLocationFilter,
  VirtualLocationFilter,
} from '../../utils/types';
import { PhysicalVirtualLocationEnum } from '../../utils/enums';

//Custom hook for calendar filtering utilities
export const useCalendarFilters = (filters: Filters) => {
  // Set initial filter values for use by the UI
  const getFilters = (
    durationAsMin: number,
    physicalLocations: Place[],
    virtualLocations: VirtualLocation[]
  ) => {
    // If this duration is not yet set as a filter, do so
    if (
      filters.durations.findIndex(
        (durationFilter: DurationFilter) =>
          durationFilter.duration === durationAsMin
      ) === -1
    ) {
      filters.durations.push({ duration: durationAsMin, isChecked: true });
    }

    // If this physical location is not yet set as a filter, do so
    physicalLocations.forEach((location) => {
      if (
        filters.physicalLocations.findIndex(
          (locationFilter: PhysicalLocationFilter) =>
            locationFilter.placeId === location.placeId
        ) === -1
      ) {
        filters.physicalLocations.push({
          placeId: location.placeId,
          name: location.name,
          address1: location.address1,
          address2: location.address2,
          city: location.city,
          state: location.state,
          postalCode: location.postalCode,
        });
      }
    });

    // If this virtual location is not yet set as a filter, do so
    virtualLocations.forEach((location) => {
      if (
        filters.virtualLocations.findIndex(
          (locationFilter: VirtualLocationFilter) =>
            locationFilter.virtualLocationId === location.virtualLocationId
        ) === -1
      ) {
        filters.virtualLocations.push({
          virtualLocationId: location.virtualLocationId,
          name: location.label,
        });
      }
    });
    return filters;
  };

  // Check if the input duration is selected on the UI or not
  const filterByMeetingLength = (
    duration: number,
    filters: Filters
  ): boolean => {
    const durationIndex = filters.durations.findIndex(
      (durationFilter: { duration: number }) =>
        durationFilter.duration === duration
    );

    if (durationIndex === -1) {
      return false;
    }

    return !filters.durations[durationIndex].isChecked;
  };

  // Check if any of the input physicalLocations are selected on the UI or not
  const filterByPhysicalLocation = (
    inputPhysicalLocations: Place[],
    filters: Filters,
    selectedLocation: SelectedLocation
  ): boolean => {
    if (selectedLocation.id === 0) return false;

    for (let i = 0; i < inputPhysicalLocations.length; i++) {
      const locationIndex = filters.physicalLocations.findIndex(
        (locationFilter) =>
          locationFilter.placeId === inputPhysicalLocations[i].placeId
      );

      if (locationIndex === -1) {
        continue;
      }

      if (
        selectedLocation.locationType ===
          PhysicalVirtualLocationEnum.physical &&
        filters.physicalLocations[locationIndex].placeId === selectedLocation.id
      ) {
        return false;
      }
    }

    return true;
  };

  // Check if any of the input virtualLocations are selected on the UI or not
  const filterByVirtualLocation = (
    inputVirtualLocations: VirtualLocation[],
    filters: Filters,
    selectedLocation: SelectedLocation
  ): boolean => {
    if (selectedLocation.id === 0) return false;

    for (let i = 0; i < inputVirtualLocations.length; i++) {
      const locationIndex = filters.virtualLocations.findIndex(
        (locationFilter) =>
          locationFilter.virtualLocationId ===
          inputVirtualLocations[i].virtualLocationId
      );

      if (locationIndex === -1) {
        continue;
      }

      if (
        selectedLocation.locationType === PhysicalVirtualLocationEnum.virtual &&
        filters.virtualLocations[locationIndex].virtualLocationId ===
          selectedLocation.id
      ) {
        return false;
      }
    }

    return true;
  };

  //Get duration in minutes from ISO time string
  const getDurationInMinutes = (duration: string): number => {
    return Duration.fromISOTime(duration).as('minutes');
  };

  return {
    getFilters,
    filterByMeetingLength,
    filterByPhysicalLocation,
    filterByVirtualLocation,
    getDurationInMinutes,
  };
};
